import { FC } from 'react';
import { Alert, AlertTitle, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLimitAndUsageControllerGetUsageQuery } from 'src/app/services/api.generated';
import { useIsDemoUser } from 'src/components/hooks/useIsDemoUser';

type Props = {
  /** Show without asking /usage (a request already came back BILLING_SUSPENDED). */
  forceShow?: boolean;
};

/**
 * Shown to every user of a company whose access to documents is suspended
 * for an unpaid invoice (BILLING_SUSPEND_ENABLED). Sign-in, lists and the
 * billing page keep working; the button leads to the Stripe portal.
 */
export const BillingSuspendedBanner: FC<Props> = ({ forceShow = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDemoUser = useIsDemoUser();
  // Same cached request as the usage card (all users).
  const { data } = useLimitAndUsageControllerGetUsageQuery(undefined as void, {
    skip: forceShow || isDemoUser,
  });

  if (!forceShow && (isDemoUser || !data?.billingSuspended)) return null;

  return (
    <Alert
      severity="error"
      variant="outlined"
      role="alert"
      data-testid="billing-suspended-banner"
      sx={{ mb: { xs: 2, md: 3 }, alignItems: 'center' }}
      action={
        <Button color="inherit" variant="outlined" size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => navigate('/manage-billing')}>
          {t('billingSuspended.cta')}
        </Button>
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>{t('billingSuspended.title')}</AlertTitle>
      {t('billingSuspended.text')}
    </Alert>
  );
};
